import { useState, useRef, ChangeEvent } from "react";
import { useNavigate } from "react-router";
import { useForm, SubmitHandler, FieldValues } from "react-hook-form";
// mui
import {
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  IconButton,
  Card,
  Stack,
  Drawer,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
// handlers
import SharedHandler, {
  addCaptchaToLocalStorage,
  addGotoNavigation,
} from "src/components/UI/Shared/handler";
import Handler, { handleRequest } from "src/components/UI/Profile/handler";

import { RESPONSE_STATUS } from "src/config/service";
import { validFileSize } from "src/components/UI/Shared/validation";
import user from "src/components/UI/Shared/user";
// components
import PersonalInfoEdit from "src/components/UI/Profile/PersonalInfoEdit";
import PersonalInfoDisplay from "src/components/UI/Profile/PersonalInfoDisplay";
import AddressEdit from "src/components/UI/Profile/AddressEdit";
import AddressDisplay from "src/components/UI/Profile/AddressDisplay";
import CreditsDisplay from "src/components/UI/Profile/Credit";
import Notification from "src/components/UI/Shared/Notification";
// assets
import user_image from "src/components/UI/Profile/assets/images/default.jpg";
import styles from "src/components/UI/Profile/assets/scss/Profile.module.scss";
import classes from "src/components/UI/Profile/assets/scss/base-widget-profile.module.scss";
// redux
import { useSelector, useDispatch } from "react-redux";
import { appActions } from "src/store/store";

const ProfileMobile = () => {
  const dispatch = useDispatch();
  const notification = useSelector((state: any) => state.app.Notification);
  const navigate = useNavigate();
  const optionalParam = useSelector((state: any) => state.app.optionalParam);
  const currentBalance = useSelector((state: any) => state.app.currentBalance);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const apiURL = process.env.REACT_APP_API_URL || "";

  const [open, setOpen] = useState(true);
  const [editDetailsMode, setEditDetailsMode] = useState(false);
  const [editAddressMode, setEditAddressMode] = useState(false);
  const [editHeadMode, setEditHeadMode] = useState(false);

  const current_user = user.getCurrentLoggedIn();
  const data = localStorage.getItem(current_user);
  const _info = data !== null ? JSON.parse(data) : null;
  const user_info = _info["session_token"];
  const email = user_info !== null ? Handler.check(user_info["email"]) : "-";
  let profile_pic = user_info !== null ? user_info["pic"] : "";

  const {
    register,
    getValues,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      user_name: user_info["user_name"],
      profession: user_info["profession"],
      first_name: user_info["first_name"],
      last_name: user_info["last_name"],
      email: user_info["email"],
      bio: user_info["bio"],
      street: user_info["street"],
      city: user_info["city"],
      state: user_info["state"],
      country: user_info["country"],
      zip_code: user_info["zip_code"],
    },
  });

  const handleClose = () => {
    setOpen(false);
    navigate("..");
  };
  const handleBack = () => {
    setEditDetailsMode(false);
    setEditHeadMode(false);
    setEditAddressMode(false);
    Handler.setValuesWhenBack(setValue);
  };
  const behaviour = async (returned: any, user_info: any) => {
    if (returned.flag === "TRACKING") {
      if (returned.message[0] === RESPONSE_STATUS.BLOCKED) {
        await dispatch(
          appActions.setNotification({
            severity: "error",
            message:
              "Service cap reached today! Take a breather and come back for more fun tomorrow!",
            open: true,
          })
        );
      } else if (returned.message[1]) {
        await addCaptchaToLocalStorage(returned.message[1]);
        dispatch(
          appActions.setTrackingData({
            serviceStatus: returned.message[0],
            captcha: returned.message[1],
          })
        );
      } else {
        dispatch(
          appActions.setTrackingData({ serviceStatus: returned.message[0] })
        );
      }
    } else if (returned.flag === "SUCCESS") {
      Handler.updateUserInfo(data, user_info, current_user);
      dispatch(
        appActions.setNotification({
          open: true,
          message: returned.message[0],
          severity: "success",
        })
      );
    } else if (returned.flag === "SUCCESS_PIC") {
      if (data) {
        Handler.updateUserPic(data, returned.message[1], current_user);
        dispatch(
          appActions.setNotification({
            open: true,
            message: returned.message[0],
            severity: "success",
          })
        );
      }
    } else if (returned.flag === "ERROR") {
      dispatch(
        appActions.setNotification({
          open: true,
          message:
            returned.message && returned.message !== ""
              ? returned.message
              : "Error occured.",
          severity: "error",
        })
      );
    }
  };
  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    addGotoNavigation("2");

    const endpointUrl = "user/updateProfilePic/";
    const timestamp = new Date().toISOString();
    const signature = SharedHandler.signRequest(
      "/" + endpointUrl,
      "POST",
      timestamp
    );

    const selectedFile = event.target.files?.[0];

    if (!selectedFile) return;
    const isValidImageSize = validFileSize(selectedFile, "image");
    if (!isValidImageSize) {
      dispatch(
        appActions.setNotification({
          open: true,
          message: "Your image can not exceed 5 MB.",
          severity: "error",
        })
      );
      return;
    }

    const formData = new FormData();
    formData.append("user_image", selectedFile);
    formData.append("user_email", email);

    const requestSent = await handleRequest(
      apiURL ? apiURL : "",
      endpointUrl,
      formData,
      signature,
      timestamp,
      optionalParam,
      "pic"
    );
    await behaviour(requestSent, user_info);
  };

  const handleSaveChanges: SubmitHandler<FieldValues> = async (data) => {
    addGotoNavigation("2");
    setEditDetailsMode(false);
    setEditHeadMode(false);
    setEditAddressMode(false);

    const endpointUrl = "user/update/";
    const timestamp = new Date().toISOString();
    const signature = SharedHandler.signRequest(
      "/" + endpointUrl,
      "POST",
      timestamp
    );

    const formData = new FormData();

    formData.append("user_name", data.user_name);
    formData.append("profession", data.profession);
    formData.append("first_name", data.first_name);
    formData.append("last_name", data.last_name);
    formData.append("old_email", email);
    formData.append("new_email", data.email);
    formData.append("bio", data.bio);
    formData.append("street", data.street);
    formData.append("city", data.city);
    formData.append("state", data.state);
    formData.append("country", data.country);
    formData.append("zip_code", data.zip_code);

    const user_info = {
      pic: profile_pic,
      user_name: data.user_name,
      profession: data.profession,
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      bio: data.bio,
      street: data.street,
      city: data.city,
      state: data.state,
      country: data.country,
      zip_code: data.zip_code,
      status: "logged-in",
    };

    const requestSent = await handleRequest(
      apiURL ? apiURL : "",
      endpointUrl,
      formData,
      signature,
      timestamp,
      optionalParam
    );
    await behaviour(requestSent, user_info);
  };
  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        variant="temporary"
        PaperProps={{
          style: {
            width: "100%",
            height: "91vh",
            backgroundColor: "#2e3840",
          },
        }}
      >
        <Paper className={classes.header}>
          {editDetailsMode || editHeadMode || editAddressMode ? (
            <KeyboardBackspaceIcon
              sx={{ color: "#c3e7ff75", cursor: "pointer" }}
              onClick={handleBack}
            />
          ) : (
            <Typography
              className={classes.headerText}
              variant="h1"
              component="h1"
            >
              {"My profile"}
            </Typography>
          )}
        </Paper>
        <IconButton
          className={classes.iconButton}
          disableRipple
          aria-label="Close"
          onClick={handleClose}
        >
          <CloseIcon
            sx={{ fontSize: "1rem", color: "#c3e7ff75", fontWeight: "900" }}
          />
        </IconButton>
        <div className={styles.sections}>
          <Container className={styles.section}>
            <KeyboardBackspaceIcon
              className={styles.backArrow}
              sx={{ fontSize: "1rem", color: "#c3e7ff75", fontWeight: "900" }}
              onClick={
                editDetailsMode || editHeadMode ? handleBack : handleClose
              }
            />

            <div className={styles.avatarContainer}>
              <div className={styles.headContainer}>
                <input
                  type="file"
                  accept="image/jpg,image/jpeg,image/png,"
                  hidden
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleImageUpload}
                />
                <Avatar
                  className={styles.avatar}
                  src={
                    profile_pic
                      ? apiURL + profile_pic.slice(1, profile_pic.length)
                      : user_image
                  }
                  onClick={handleAvatarClick}
                  style={{ cursor: "pointer" }}
                />
                {editHeadMode ? (
                  <TextField
                    {...register("user_name", {
                      required: "User name is required.",
                      pattern: {
                        value: /^[A-Za-z][A-Za-z_]*$/,
                        message:
                          "User name should begin with a letter. User name accepts letters and _ only.",
                      },
                      minLength: {
                        value: 3,
                        message: "User name should be at least 3 characters.",
                      },
                      maxLength: {
                        value: 50,
                        message: "User name should not exceed 50 characters.",
                      },
                    })}
                    type="text"
                    className={styles.editField}
                    error={!!errors.user_name}
                    helperText={errors.user_name?.message?.toString()}
                  />
                ) : (
                  <Typography className={styles.heading} variant="h5">
                    {getValues("user_name")}
                  </Typography>
                )}
              </div>
              {editHeadMode ? (
                <TextField
                  {...register("profession")}
                  type="text"
                  className={styles.editField}
                  placeholder="profession"
                  error={!!errors.profession}
                  helperText={errors.profession?.message?.toString()}
                />
              ) : (
                <Typography className={styles.info} variant="subtitle2">
                  {getValues("profession")}
                </Typography>
              )}
              <Typography className={styles.info} variant="subtitle2">
                {Handler.getCompositeAddress(getValues)}
              </Typography>
            </div>
            <div className={styles.bioDiv}>
              {editHeadMode ? (
                <Button
                  className={styles.saveButton}
                  variant="outlined"
                  type="submit"
                  onClick={handleSubmit(handleSaveChanges)}
                  disabled={
                    Boolean(errors.user_name?.message) ||
                    Boolean(errors.profession?.message)
                  }
                  style={{ justifyContent: "flex-end" }}
                >
                  Save changes
                </Button>
              ) : (
                <Button
                  className={`${
                    editDetailsMode || editAddressMode
                      ? styles.buttonForEdit
                      : styles.editButton
                  }`}
                  variant="outlined"
                  onClick={() => {
                    setEditHeadMode(true);
                    setEditDetailsMode(false);
                    setEditAddressMode(false);
                    Handler.setValuesWhenBack(setValue);
                  }}
                  endIcon={<BorderColorOutlinedIcon />}
                >
                  {editDetailsMode || editAddressMode ? "" : "Edit"}
                </Button>
              )}
            </div>
          </Container>
          <Card
            className={styles.sectionCard}
            style={{
              maxHeight: "25rem",
              minHeight: "12rem",
            }}
          >
            <Stack spacing={0.25} alignItems={"flex-start"} sx={{ p: 0.75 }}>
              <div className={styles.detailsHeading} style={{}}>
                <Typography className={styles.detailsHeader} variant="h5">
                  Personal information
                </Typography>
                {!editDetailsMode && (
                  <Button
                    className={styles.editButton2}
                    variant="outlined"
                    onClick={() => {
                      setEditDetailsMode(true);
                      setEditHeadMode(false);
                      setEditAddressMode(false);
                      Handler.setValuesWhenBack(setValue);
                    }}
                    endIcon={<BorderColorOutlinedIcon />}
                  >
                    Edit
                  </Button>
                )}
              </div>
              <div className={styles.detailsContent}>
                {editDetailsMode ? (
                  <>
                    <PersonalInfoEdit errors={errors} register={register} />
                  </>
                ) : (
                  <PersonalInfoDisplay getValues={getValues} />
                )}
              </div>
            </Stack>
          </Card>
          <Card
            className={styles.sectionCard}
            sx={{
              minHeight: "11.5rem",
              marginBottom: "2rem",
            }}
          >
            <Stack spacing={0.25} alignItems={"flex-start"} sx={{ p: 0.75 }}>
              <div className={styles.detailsHeading}>
                <Typography className={styles.detailsHeader} variant="h5">
                  Address
                </Typography>
                {!editAddressMode && (
                  <Button
                    className={styles.editButton2}
                    variant="outlined"
                    onClick={() => {
                      setEditAddressMode(true);
                      setEditDetailsMode(false);
                      setEditHeadMode(false);
                      Handler.setValuesWhenBack(setValue);
                    }}
                    endIcon={<BorderColorOutlinedIcon />}
                  >
                    Edit
                  </Button>
                )}
              </div>
              <div className={styles.detailsContent}>
                {editAddressMode ? (
                  <AddressEdit errors={errors} register={register} />
                ) : (
                  <AddressDisplay getValues={getValues} />
                )}
              </div>
            </Stack>
          </Card>
          {(editAddressMode || editDetailsMode) && (
            <div className={styles.saveChangesDiv}>
              <Button
                className={styles.saveButton}
                variant="outlined"
                onClick={handleSubmit(handleSaveChanges)}
                style={{ marginTop: "0.5rem" }}
                disabled={
                  Boolean(errors.first_name?.message) ||
                  Boolean(errors.last_name?.message) ||
                  Boolean(errors.email?.message) ||
                  Boolean(errors.bio?.message) ||
                  Boolean(errors.street?.message) ||
                  Boolean(errors.city?.message) ||
                  Boolean(errors.state?.message) ||
                  Boolean(errors.country?.message) ||
                  Boolean(errors.zip_code?.message)
                }
              >
                Save changes
              </Button>
            </div>
          )}
          <Card
            className={styles.sectionCard}
            style={{
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              paddingLeft: "0.25rem",
            }}
            sx={{
              minHeight: "20vh",
              maxHeight: "30vh",
            }}
          >
            <CreditsDisplay
              balance={currentBalance}
              optionalParam={optionalParam}
            />
          </Card>
        </div>
      </Drawer>
      {notification.open && <Notification />}
    </>
  );
};
export default ProfileMobile;
